import React, { useState, useMemo } from 'react';
import { Package, Warehouse, Search, BarChart3 } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import CustomPieChart from 'components/common/CustomPieChart';
import { abaixoDoMinimo, minimoEfetivo } from 'features/infraestrutura/utils/estoqueMinimo';

/**
 * Props de estoque mínimo são OPCIONAIS de propósito: sem elas o componente se comporta
 * exatamente como antes. É o que permite a tela de Estoque (que também o consome, alternando
 * entre Geral e Infra) seguir intocada enquanto só Material Base recebe a funcionalidade.
 */
const ProdutosAlmoxarifadosFiltradosTable = ({
  data,
  loading,
  minimos = null,
  onEditarMinimo = null,
  somenteAbaixoDoMinimo = false,
  titulo = 'Produtos Almoxarifados Filtrados',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const minimosAtivos = minimos instanceof Map;

  // Lista de almoxarifados únicos dos dados
  const almoxarifados = useMemo(() => {
    if (data.length === 0) return [];
    const unique = [...new Set(data.map(item => item.Almoxarifado))].filter(almoxarifado =>
      almoxarifado && almoxarifado.trim() !== ''
    );
    return unique.sort();
  }, [data]);

  // A coluna é a descrição, mas a configuração casa por id — a descrição é editável no IXC.
  const idPorAlmoxarifado = useMemo(() => {
    const mapa = {};
    data.forEach(item => {
      if (item.Almoxarifado && item.id_almox !== null && item.id_almox !== undefined) {
        mapa[item.Almoxarifado] = item.id_almox;
      }
    });
    return mapa;
  }, [data]);

  // Agrupar dados por produto para criar estrutura similar aos equipamentos
  const dadosAgrupados = useMemo(() => {
    const agrupados = {};
    
    data.forEach(item => {
      const chave = `${item.id_produto}-${item.Produto}`;
      if (!agrupados[chave]) {
        agrupados[chave] = {
          id_produto: item.id_produto,
          Produto: item.Produto,
          Saldo: 0
        };
        // Inicializar todas as colunas de almoxarifados com 0
        almoxarifados.forEach(almoxarifado => {
          agrupados[chave][almoxarifado] = 0;
        });
      }
      
      // Adicionar o saldo do almoxarifado específico
      agrupados[chave][item.Almoxarifado] = parseFloat(item.Saldo || 0);
      agrupados[chave].Saldo += parseFloat(item.Saldo || 0);
    });
    
    return Object.values(agrupados);
  }, [data, almoxarifados]);

  // Função para extrair o nome da cidade do almoxarifado (Nova abordagem)
  const getCityName = (almoxarifado) => {
    if (!almoxarifado || typeof almoxarifado !== 'string') return 'N/A';
    
    const cleanName = almoxarifado.trim();
    
    // Nova abordagem: pegar tudo que vem após "FIBRA" ou "TELECOM"
    const upperName = cleanName.toUpperCase();
    
    // Procurar por "FIBRA" e pegar o que vem depois
    const fibraIndex = upperName.indexOf('FIBRA');
    if (fibraIndex !== -1) {
      const afterFibra = cleanName.substring(fibraIndex + 5).trim();
      return afterFibra || cleanName;
    }
    
    // Procurar por "TELECOM" e pegar o que vem depois
    const telecomIndex = upperName.indexOf('TELECOM');
    if (telecomIndex !== -1) {
      const afterTelecom = cleanName.substring(telecomIndex + 7).trim();
      return afterTelecom || cleanName;
    }
    
    // Se não encontrou FIBRA nem TELECOM, usar lógica genérica
    return cleanName;
  };

  // Filtrar dados
  const filteredData = useMemo(() => {
    return dadosAgrupados.filter(item => {
      const matchesSearch = (item.Produto && String(item.Produto).toLowerCase().includes(searchTerm.toLowerCase())) ||
                           item.id_produto.toString().includes(searchTerm);

      if (!matchesSearch) {
        return false;
      }

      if (!somenteAbaixoDoMinimo || !minimosAtivos) {
        return true;
      }

      return almoxarifados.some(almoxarifado => abaixoDoMinimo(
        minimos,
        item.id_produto,
        idPorAlmoxarifado[almoxarifado],
        item[almoxarifado],
      ));
    });
  }, [dadosAgrupados, searchTerm, somenteAbaixoDoMinimo, minimosAtivos, minimos, almoxarifados, idPorAlmoxarifado]);

  // Calcular KPIs
  const kpis = useMemo(() => {
    const totalProdutos = filteredData.length;
    const totalAlmoxarifados = new Set(
      filteredData.flatMap(item => 
        almoxarifados.filter(almoxarifado => parseFloat(item[almoxarifado] || 0) > 0)
      )
    ).size;
    const saldoTotal = filteredData.reduce((total, item) => total + parseFloat(item.Saldo || 0), 0);
    const produtosComSaldo = filteredData.filter(item => parseFloat(item.Saldo || 0) > 0).length;
    
    return {
      totalProdutos,
      totalAlmoxarifados,
      saldoTotal,
      produtosComSaldo
    };
  }, [filteredData, almoxarifados]);

  // Dados para gráfico de pizza - Distribuição por almoxarifado
  const distribuicaoAlmoxarifado = useMemo(() => {
    const distribuicao = {};
    almoxarifados.forEach(almoxarifado => {
      distribuicao[almoxarifado] = filteredData.reduce((total, item) => 
        total + parseFloat(item[almoxarifado] || 0), 0
      );
    });
    
    return Object.entries(distribuicao)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({ name: getCityName(name), value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData, almoxarifados]);

  // Configurar colunas da tabela
  const columns = [
    {
      key: 'id_produto',
      label: 'ID',
      sortable: true,
      className: 'w-[8%]',
      render: (value) => (
        <span className="font-mono text-xs text-[color:var(--text-muted)]">#{value}</span>
      )
    },
    {
      key: 'Produto',
      label: 'Produto',
      sortable: true,
      className: 'w-[25%]',
      render: (value) => (
        <div className="font-medium text-[color:var(--text-primary)] text-xs break-words" title={value}>
          {value}
        </div>
      )
    },
    ...almoxarifados.map(almoxarifado => {
      const cityName = getCityName(almoxarifado);
      const idAlmox = idPorAlmoxarifado[almoxarifado];

      return {
        key: almoxarifado,
        label: cityName,
        sortable: true,
        className: 'w-[10%] text-xs',
        render: (value, row) => {
          const quantidade = parseFloat(value || 0);
          const isZero = quantidade === 0;
          const formatado = quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

          const efetivo = minimosAtivos ? minimoEfetivo(minimos, row?.id_produto, idAlmox) : null;
          const critico = minimosAtivos && abaixoDoMinimo(minimos, row?.id_produto, idAlmox, quantidade);

          let cor = isZero ? 'text-[color:var(--text-muted)]' : 'text-[color:var(--status-good)]';
          if (critico) {
            cor = 'text-rose-500 font-semibold';
          }

          const titulo = efetivo !== null && efetivo > 0
            ? `${cityName}: ${formatado} · mínimo ${efetivo.toLocaleString('pt-BR')}`
            : `${cityName}: ${formatado}`;

          return (
            <div className={`text-right ${cor}`} title={titulo}>
              <span className="font-mono text-xs">{formatado}</span>
              {efetivo !== null && efetivo > 0 ? (
                <span className="ml-1 font-mono text-[10px] text-[color:var(--text-muted)]">
                  /{efetivo.toLocaleString('pt-BR')}
                </span>
              ) : null}
            </div>
          );
        }
      };
    }),
    {
      key: 'Saldo',
      label: 'Total',
      sortable: true,
      className: 'w-[8%]',
      render: (value) => {
        const total = parseFloat(value || 0);
        return (
          <div className="text-right font-bold text-[color:var(--accent)]" title={`Total: ${total.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}>
            <span className="font-mono text-xs">
              {total.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[color:var(--border)] border-t-[color:var(--accent)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[color:var(--surface)] p-4 rounded-lg shadow-sm border border-[color:var(--border)]">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-[color:var(--accent)]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Total Produtos</p>
              <p className="text-2xl font-semibold text-[color:var(--text-primary)]">{kpis.totalProdutos}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[color:var(--surface)] p-4 rounded-lg shadow-sm border border-[color:var(--border)]">
          <div className="flex items-center">
            <Warehouse className="h-8 w-8 text-[color:var(--status-good)]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Almoxarifados</p>
              <p className="text-2xl font-semibold text-[color:var(--text-primary)]">{kpis.totalAlmoxarifados}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[color:var(--surface)] p-4 rounded-lg shadow-sm border border-[color:var(--border)]">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-[color:var(--accent)]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Saldo Total</p>
              <p className="text-2xl font-semibold text-[color:var(--text-primary)]">
                {kpis.saldoTotal.toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-[color:var(--surface)] p-4 rounded-lg shadow-sm border border-[color:var(--border)]">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-[color:var(--status-warn)]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Com Saldo</p>
              <p className="text-2xl font-semibold text-[color:var(--text-primary)]">{kpis.produtosComSaldo}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Distribuição */}
      {distribuicaoAlmoxarifado.length > 0 && (
        <div className="bg-[color:var(--surface)] p-6 rounded-lg shadow-sm border border-[color:var(--border)]">
          <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-4">
            Distribuição por Almoxarifado
          </h3>
          <CustomPieChart 
            data={distribuicaoAlmoxarifado} 
            useEnhancedLegend={true}
            maxLegendItems={6}
            sortDescending={true}
          />
        </div>
      )}

      {/* Tabela */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="px-6 py-4 border-b border-[color:var(--border)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-medium text-[color:var(--text-primary)]">
              {titulo}
              <span className="ml-2 text-sm text-[color:var(--text-muted)]">
                ({filteredData.length} de {dadosAgrupados.length} produtos)
              </span>
            </h3>
            
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[color:var(--text-muted)]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar produto ou ID..."
                className="px-3 py-2 border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] text-sm w-64"
              />
            </div>
          </div>
        </div>
        
        <DataTable
          data={filteredData}
          columns={columns}
          loading={loading}
          searchable={false}
          pagination={true}
          pageSize={20}
          onRowClick={onEditarMinimo ? (row) => onEditarMinimo(row) : null}
          emptyMessage={somenteAbaixoDoMinimo
            ? 'Nenhum produto abaixo do mínimo.'
            : 'Nenhum dado encontrado'}
        />
      </div>
    </div>
  );
};

export default ProdutosAlmoxarifadosFiltradosTable;


